
import { Link } from 'react-router-dom'
import { Heart, Truck, Building2, ArrowRight, Gift, Clock, Leaf, Users, Sparkles, MapPin, BookOpen, CheckCircle2 } from 'lucide-react'
import MapComponent from '../MapComponent'
import { useUser } from '../../contexts/UserContext'
import { communityImages, successStoryImages } from '../../assets/images'
import { getStories, Story } from '../../api/stories'
import { useEffect, useState } from 'react'
import { resolveImageUrl, getStaticImageUrl } from '../../utils/imageUrl'
import ApiImage from '../ApiImage'

const PublicHomePage = () => {
  const { isAuthenticated } = useUser()
  const [latestStories, setLatestStories] = useState<Story[]>([])

  useEffect(() => {
    const load = async () => {
      const stories = await getStories()
      setLatestStories(stories.slice(0, 3))
    }
    load()
  }, [])

  return (
    <div className="min-h-screen relative overflow-x-hidden bg-neutral-50">
      {/* Hero Section with Professional Gradient */}
      <section className="relative section-lg hero-mesh overflow-hidden">
        {/* Subtle Background Elements */}
        <div className="absolute inset-0 opacity-40 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-primary-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-secondary-200 rounded-full blur-3xl"></div>
        </div>
        
        <div className="container relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left: Hero Content */}
            <div className="text-center lg:text-left animate-fade-in overflow-visible">
              <span className="section-eyebrow mb-6 inline-flex">
                <Leaf className="h-3.5 w-3.5" />
                Community food rescue
              </span>
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-neutral-900 mb-6 leading-snug md:leading-[1.15] tracking-tight overflow-visible">
                {/* Keep a real space between lines so crawlers don't read "CommunitiesThrough" */}
                <span className="block">Connecting Communities{' '}</span>
                <span className="block mt-2 pb-1.5 md:pb-2 leading-normal bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 bg-clip-text text-transparent">
                  Through Food
                </span>
              </h1>
              <p className="text-lg md:text-xl text-neutral-600 mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                HarvestHelp links food banks, suppliers, and volunteers so surplus food reaches families instead of landfills.
              </p>
              {!isAuthenticated && (
                <>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-8">
                    <Link
                      to="/register"
                      className="btn-primary inline-flex items-center justify-center group whitespace-nowrap px-8 py-3.5 text-lg shadow-glow"
                    >
                      <Gift className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform duration-200" />
                      Get Started
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                    </Link>
                    <Link to="/our-story" className="btn-outline btn-lg inline-flex items-center justify-center">
                      Our Story
                    </Link>
                  </div>
                  <ul className="flex flex-col sm:flex-row flex-wrap gap-3 justify-center lg:justify-start mb-10">
                    {['Reduce local food waste', 'Coordinate real-time pickups', 'Support families in need'].map((point) => (
                      <li key={point} className="inline-flex items-center gap-2 text-sm text-neutral-700 bg-white/80 backdrop-blur-sm border border-neutral-200/80 rounded-full px-4 py-2 shadow-soft">
                        <CheckCircle2 className="h-4 w-4 text-primary-600 shrink-0" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-xl mx-auto lg:mx-0">
                    {[
                      { Icon: Users, label: 'Volunteer', desc: 'Deliver food locally', color: 'text-accent-600 bg-accent-50 border-accent-100' },
                      { Icon: Truck, label: 'Supplier', desc: 'Donate surplus food', color: 'text-secondary-600 bg-secondary-50 border-secondary-100' },
                      { Icon: Building2, label: 'Food bank', desc: 'Receive donations', color: 'text-primary-600 bg-primary-50 border-primary-100' },
                    ].map(({ Icon, label, desc, color }) => (
                      <div key={label} className={`flex flex-col p-4 rounded-2xl border ${color}`}>
                        <Icon className="h-5 w-5 mb-2" aria-hidden="true" />
                        <span className="font-semibold text-neutral-900 text-sm">{label}</span>
                        <span className="text-xs text-neutral-600 mt-0.5">{desc}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Right: Hero Image */}
            <div className="relative animate-slide-in-right">
              <div className="relative overflow-hidden group bg-white rounded-3xl p-6 shadow-xl border border-neutral-100">
                <div className="relative w-full h-80 rounded-2xl overflow-hidden bg-gradient-to-br from-primary-50 to-secondary-50 ring-1 ring-neutral-100">
                  <ApiImage
                    src="donating-food.jpg"
                    alt="Community food sharing"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-md rounded-2xl px-4 py-3 shadow-lg z-10 border border-white/50">
                    <Heart className="h-6 w-6 text-primary-600" fill="currentColor" />
                  </div>
                </div>
                <a
                  href="#community-map"
                  className="mt-4 mb-4 inline-flex items-center gap-2 bg-primary-50 hover:bg-primary-100/80 rounded-full px-3 py-2 border border-primary-100 transition-colors"
                >
                  <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-neutral-700 whitespace-nowrap">Live community map</span>
                </a>
                <div className="text-center">
                  <h3 className="text-xl font-bold text-neutral-900 mb-2">Community Impact</h3>
                  <p className="text-neutral-600 leading-relaxed text-sm md:text-base">Join volunteers helping reduce food waste and feed families in need.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section with Images */}
      <section className="section bg-white">
        <div className="container">
          <div className="text-center mb-14 md:mb-16">
            <span className="section-eyebrow mb-4 inline-flex"><Sparkles className="h-3.5 w-3.5" />Simple process</span>
            <h2 className="section-heading mb-4">How It Works</h2>
            <div className="section-underline mx-auto"></div>
            <p className="text-lg md:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed mt-6">
              A simple flow connecting suppliers, volunteers, and food banks to reduce waste and feed communities
            </p>
            <Link to="/how-it-works" className="btn-outline inline-flex items-center mt-6">
              Full role-by-role guide
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
          
          {/* Linear Flow: Suppliers → Volunteers → Food Banks */}
          <div className="flex flex-col md:flex-row items-center justify-center gap-6 md:gap-8">
            {/* Suppliers */}
            <Link to="/suppliers" className="block group flex-1 max-w-sm">
              <div className="card-interactive relative overflow-hidden rounded-2xl h-full">
                <div className="relative mb-6 overflow-hidden rounded-2xl">
                  <ApiImage
                    src="whole-foods.webp"
                    alt="Whole Foods supplier"
                    className="w-full h-56 object-cover rounded-2xl group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary-900/30 to-transparent rounded-2xl"></div>
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md rounded-2xl p-3 shadow-lg border border-white/50">
                    <Truck className="h-6 w-6 text-secondary-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">Suppliers</h3>
                <p className="text-neutral-600 mb-4 leading-relaxed">Donate surplus food and reduce waste while helping your community</p>
                <div className="flex items-center text-sm text-secondary-600 font-medium">
                  <Leaf className="h-4 w-4 mr-2" />
                  <span>Reduce waste</span>
                </div>
              </div>
            </Link>

            {/* Arrow 1 */}
            <div className="hidden md:flex items-center justify-center text-secondary-600">
              <ArrowRight className="h-8 w-8" />
            </div>
            <div className="md:hidden flex items-center justify-center text-secondary-600 py-4">
              <ArrowRight className="h-8 w-8 rotate-90" />
            </div>

            {/* Volunteers */}
            <Link to="/volunteers" className="block group flex-1 max-w-sm">
              <div className="card-interactive relative overflow-hidden rounded-2xl h-full">
                <div className="relative mb-6 overflow-hidden rounded-2xl">
                  <ApiImage
                    src="me.jpeg"
                    alt="Volunteer helping"
                    className="w-full h-56 object-cover rounded-2xl group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-accent-900/30 to-transparent rounded-2xl"></div>
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md rounded-2xl p-3 shadow-lg border border-white/50">
                    <Users className="h-6 w-6 text-accent-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">Volunteers</h3>
                <p className="text-neutral-600 mb-4 leading-relaxed">Transport donations from suppliers to food banks in your community</p>
                <div className="flex items-center text-sm text-accent-600 font-medium">
                  <Truck className="h-4 w-4 mr-2" />
                  <span>Make deliveries</span>
                </div>
              </div>
            </Link>

            {/* Arrow 2 */}
            <div className="hidden md:flex items-center justify-center text-accent-600">
              <ArrowRight className="h-8 w-8" />
            </div>
            <div className="md:hidden flex items-center justify-center text-accent-600 py-4">
              <ArrowRight className="h-8 w-8 rotate-90" />
            </div>

            {/* Food Banks */}
            <Link to="/food-banks" className="block group flex-1 max-w-sm">
              <div className="card-interactive relative overflow-hidden rounded-2xl h-full">
                <div className="relative mb-6 overflow-hidden rounded-2xl">
                  <ApiImage
                    src="food-bank-of-enc.jpg"
                    alt="Food Bank of ENC operations"
                    className="w-full h-56 object-cover rounded-2xl group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-primary-900/30 to-transparent rounded-2xl"></div>
                  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur-md rounded-2xl p-3 shadow-lg border border-white/50">
                    <Building2 className="h-6 w-6 text-primary-600" />
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-neutral-900 mb-3">Food Banks</h3>
                <p className="text-neutral-600 mb-4 leading-relaxed">Receive donations and coordinate volunteers to distribute food to those in need</p>
                <div className="flex items-center text-sm text-primary-600 font-medium">
                  <Clock className="h-4 w-4 mr-2" />
                  <span>24/7 coordination</span>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Stories (real content) */}
      <section className="section bg-gradient-to-br from-neutral-50/80 via-white to-primary-50/40">
        <div className="container">
          <div className="text-center mb-14 md:mb-16">
            <span className="section-eyebrow mb-4 inline-flex"><BookOpen className="h-3.5 w-3.5" />From the stories</span>
            <h2 className="section-heading mb-4">Latest Stories</h2>
            <div className="section-underline mx-auto"></div>
            <p className="text-lg md:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed mt-6">
              Real updates and stories from HarvestHelp.
            </p>
          </div>

          {latestStories.length === 0 ? (
            <div className="text-center bg-white rounded-2xl shadow-soft border border-neutral-200/80 p-10 md:p-12 max-w-lg mx-auto">
              <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                <BookOpen className="h-7 w-7 text-primary-600" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">No posts yet</h3>
              <p className="text-neutral-600 mb-6">Check back soon for community stories and updates.</p>
              <Link to="/blog" className="btn-primary inline-flex items-center">
                View Stories
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {latestStories.map((story, idx) => {
                const initialSrc = story.imageUrl ? resolveImageUrl(story.imageUrl) : (idx % 2 === 0 ? communityImages.communityGathering4 : successStoryImages.successStory1)
                return (
                <Link key={story.id} to={`/stories/${story.id}`} className="block group">
                  <div className="card-interactive overflow-hidden bg-white rounded-2xl h-full flex flex-col">
                    <div className="relative overflow-hidden rounded-t-2xl">
                      <img
                        src={initialSrc}
                        alt={story.title}
                        className="w-full h-56 object-cover group-hover:scale-105 transition-transform duration-700"
                        onError={(e) => {
                          const staticUrl = story.imageUrl ? getStaticImageUrl(story.imageUrl) : ''
                          if (staticUrl && e.currentTarget.src !== staticUrl) {
                            e.currentTarget.src = staticUrl
                            return
                          }
                          e.currentTarget.src = idx % 2 === 0 ? communityImages.communityGathering4 : successStoryImages.successStory1
                        }}
                      />
                    </div>
                    <div className="p-6 flex flex-col flex-1">
                      <h3 className="text-xl font-semibold text-neutral-900 mb-3 line-clamp-2">
                        {story.title}
                      </h3>
                      <p className="text-neutral-600 mb-4 leading-relaxed line-clamp-3">
                        {story.excerpt || story.content.substring(0, 140) + '...'}
                      </p>
                      <div className="text-sm text-neutral-500 font-medium">
                        {story.authorName}
                      </div>
                    </div>
                  </div>
                </Link>
              )})}
            </div>
          )}

          <div className="text-center mt-10">
            <Link to="/blog" className="btn-outline inline-flex items-center">
              View all stories
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <p className="mt-6 text-sm text-neutral-500">
              Exploring hunger research? Visit{' '}
              <a
                href="https://globalhungerdashboard.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-600 font-medium hover:underline"
              >
                Global Hunger Dashboard
              </a>
              .
            </p>
          </div>
        </div>
      </section>

      {/* Map + Impact */}
      <section id="community-map" className="section bg-gradient-to-br from-neutral-50/80 via-white to-primary-50/40 relative scroll-mt-24">
        <div className="container">
          <div className="text-center mb-14 md:mb-16">
            <span className="section-eyebrow mb-4 inline-flex"><MapPin className="h-3.5 w-3.5" />Near you</span>
            <h2 className="section-heading mb-4">Find Resources Near You</h2>
            <div className="section-underline mx-auto"></div>
            <p className="text-lg md:text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed mt-6">
              Explore food banks and suppliers on the map — no account required.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6 md:p-8 overflow-hidden">
                <MapComponent height="h-[400px]" />
                <div className="flex flex-wrap justify-center gap-6 text-sm text-neutral-600 mt-6 pt-6 border-t border-neutral-100">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-primary-500 rounded-full"></div>
                    <span>Food Banks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-secondary-500 rounded-full"></div>
                    <span>Suppliers</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6 md:p-8">
              <h3 className="text-lg font-bold text-neutral-900 mb-6 flex items-center gap-2">
                <Heart className="h-5 w-5 text-primary-500" />
                Community Impact
              </h3>
              <div className="space-y-4">
                <div className="p-4 bg-primary-50 rounded-xl border border-primary-100/50">
                  <div className="text-sm font-semibold text-primary-700">Less waste</div>
                  <div className="text-sm text-primary-700 mt-0.5">More food reaching families</div>
                </div>
                <div className="p-4 bg-secondary-50 rounded-xl border border-secondary-100/50">
                  <div className="text-sm font-semibold text-secondary-700">Faster coordination</div>
                  <div className="text-sm text-secondary-700 mt-0.5">Clear pickup and drop-off flow</div>
                </div>
                <div className="p-4 bg-accent-50 rounded-xl border border-accent-100/50">
                  <div className="text-sm font-semibold text-accent-700">Growing community</div>
                  <div className="text-sm text-accent-700 mt-0.5">Suppliers, food banks, volunteers</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional CTA Section */}
      <section className="section-sm bg-gradient-to-r from-primary-600 via-primary-700 to-secondary-600 relative overflow-hidden">
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 h-20 bg-white rounded-full blur-2xl"></div>
          <div className="absolute bottom-10 right-10 w-32 h-32 bg-white rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="container-sm text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            {isAuthenticated ? 'Welcome to HarvestHelp!' : 'Ready to Make a Difference?'}
          </h2>
          <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto leading-relaxed">
            {isAuthenticated 
              ? 'You\'re part of our community reducing food waste and feeding those in need. Together, we\'re building a more sustainable future.'
              : 'Join our community reducing food waste and feeding those in need. Together, we can build a more sustainable future.'
            }
          </p>
          {!isAuthenticated && (
            <>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/register"
                  className="bg-white text-primary-600 hover:bg-neutral-50 font-semibold px-8 py-4 rounded-xl inline-flex items-center justify-center transition-all duration-200 shadow-medium hover:shadow-large transform hover:-translate-y-0.5"
                >
                  <Gift className="mr-2 h-5 w-5" />
                  Join HarvestHelp
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
                <Link
                  to="/blog"
                  className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold px-8 py-4 rounded-xl transition-all duration-200 inline-flex items-center justify-center"
                >
                  Read Stories
                </Link>
              </div>
              <p className="mt-6 text-primary-100/90 text-sm">
                Already have an account?{' '}
                <Link to="/login" className="text-white font-semibold underline underline-offset-2 hover:text-primary-50">
                  Sign in
                </Link>
              </p>
            </>
          )}
          {isAuthenticated && (
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/dashboard"
                className="bg-white text-primary-600 hover:bg-neutral-50 font-semibold px-8 py-4 rounded-xl inline-flex items-center justify-center transition-all duration-200 shadow-medium hover:shadow-large transform hover:-translate-y-0.5"
              >
                <Heart className="mr-2 h-5 w-5" />
                Go to Dashboard
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
              <Link
                to="/blog"
                className="border-2 border-white text-white hover:bg-white hover:text-primary-600 font-semibold px-8 py-4 rounded-xl transition-all duration-200"
              >
                Read Stories
              </Link>
            </div>
          )}
        </div>
      </section>


    </div>
  )
}

export default PublicHomePage